import { useEffect, useState, type MouseEvent } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ChevronDownIcon from '@/components/ui/icons/ChevronDownIcon';
import { quickLinks } from '@/features/home/data';
import { scrollHomeTo } from '@/features/home/hooks/useHomeSmoothScroll';
import { useAppLocale } from '@/i18n/useAppLocale';
import { classNames } from '@/utils/classNames';

import styles from './QuickMenu.module.scss';

const HOME_HERO_SELECTOR = '[data-testid="home-hero-section"]';
const TOP_BUTTON_VISIBILITY_SCROLL_THRESHOLD = 4;

const handlePlaceholderLinkClick = (
  event: MouseEvent<HTMLAnchorElement>,
  isPlaceholder?: boolean,
) => {
  if (!isPlaceholder) {
    return;
  }

  event.preventDefault();
};

const handleHashLinkClick = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
  if (!href.startsWith('#') || href.length <= 1) {
    return;
  }

  const target = document.getElementById(href.slice(1));

  if (!target) {
    return;
  }

  event.preventDefault();

  scrollHomeTo(target.getBoundingClientRect().top + window.scrollY, {
    lock: true,
  });
};

const QuickMenu = () => {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('quickMenu');
  const { localizePath } = useAppLocale();
  const [isHeroQuickMenuMode, setIsHeroQuickMenuMode] = useState(false);
  const [isTopButtonVisible, setIsTopButtonVisible] = useState(false);

  useEffect(() => {
    const getScrollTop = () => {
      return document.scrollingElement ? document.scrollingElement.scrollTop : window.pageYOffset;
    };

    const updateHeroQuickMenuMode = () => {
      const scrollTop = getScrollTop();
      const heroSection = document.querySelector<HTMLElement>(HOME_HERO_SELECTOR);

      setIsTopButtonVisible(scrollTop > TOP_BUTTON_VISIBILITY_SCROLL_THRESHOLD);

      if (!heroSection) {
        setIsHeroQuickMenuMode(false);
        return;
      }

      const heroRect = heroSection.getBoundingClientRect();
      const nextHeroQuickMenuMode =
        scrollTop <= TOP_BUTTON_VISIBILITY_SCROLL_THRESHOLD &&
        heroRect.bottom > 0 &&
        heroRect.top < window.innerHeight;

      setIsHeroQuickMenuMode(nextHeroQuickMenuMode);
    };

    updateHeroQuickMenuMode();

    window.addEventListener('scroll', updateHeroQuickMenuMode, { passive: true });
    window.addEventListener('resize', updateHeroQuickMenuMode);

    return () => {
      window.removeEventListener('scroll', updateHeroQuickMenuMode);
      window.removeEventListener('resize', updateHeroQuickMenuMode);
      setIsHeroQuickMenuMode(false);
      setIsTopButtonVisible(false);
    };
  }, []);

  return (
    <div className={styles['quickMenu']}>
      <ul
        className={classNames(styles['linkList'], isHeroQuickMenuMode && styles['linkListOnHero'])}
      >
        {quickLinks.map((item) => {
          const label = t(item.id);

          return (
            <li key={item.id}>
              {item.to ? (
                <Link
                  aria-disabled={item.isPlaceholder || undefined}
                  onClick={(event) => {
                    handlePlaceholderLinkClick(event, item.isPlaceholder);
                  }}
                  target={item.target}
                  to={localizePath(item.to)}
                >
                  <img alt={label} src={item.imageSrc} />
                  <span>
                    <span className={styles['line']}>{label}</span>
                  </span>
                </Link>
              ) : (
                <a
                  aria-disabled={item.isPlaceholder || undefined}
                  href={localizePath(item.href)}
                  onClick={(event) => {
                    handlePlaceholderLinkClick(event, item.isPlaceholder);
                    handleHashLinkClick(event, item.href);
                  }}
                  rel={item.target === '_blank' ? 'noreferrer' : undefined}
                  target={item.target}
                >
                  <img alt={label} src={item.imageSrc} />
                  <span>
                    <span className={styles['line']}>{label}</span>
                  </span>
                </a>
              )}
            </li>
          );
        })}
      </ul>

      <button
        aria-label={tCommon('pageTop')}
        aria-hidden={!isTopButtonVisible}
        className={classNames(
          styles['topButton'],
          !isTopButtonVisible && styles['topButtonHidden'],
        )}
        disabled={!isTopButtonVisible}
        onClick={() => {
          scrollHomeTo(0);
        }}
        tabIndex={isTopButtonVisible ? 0 : -1}
        type='button'
      >
        <ChevronDownIcon aria-hidden='true' className={styles['topIcon']} />
      </button>
    </div>
  );
};

export default QuickMenu;
